import { useQuery } from '@tanstack/react-query';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { tenantsService } from '@/services/platform/tenants';
import { organizationsService } from '@/services/platform/organizations';
import { apiClientsService } from '@/services/platform/apiClients';

interface BaseProps {
  value?: string;
  onChange: (id: string) => void;
  id?: string;
  placeholder?: string;
  required?: boolean;
}

interface TenantSelectProps extends BaseProps {}

export function TenantSelect({ value, onChange, id, placeholder = 'Select tenant…' }: TenantSelectProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['platform', 'tenants', 'select'],
    queryFn: () => tenantsService.list({ pageSize: 100 }),
  });
  const items = data?.items ?? [];

  return (
    <Select value={value} onValueChange={onChange} disabled={isLoading}>
      <SelectTrigger id={id}>
        <SelectValue placeholder={error ? 'Failed to load tenants' : placeholder} />
      </SelectTrigger>
      <SelectContent>
        {items.map((t) => (
          <SelectItem key={t.id} value={t.id}>
            <span className="font-mono text-xs text-muted-foreground">{t.code}</span>
            <span className="ml-2">{t.name}</span>
          </SelectItem>
        ))}
        {!isLoading && items.length === 0 && (
          <div className="px-2 py-3 text-center text-xs text-muted-foreground">
            No tenants found. Onboard a tenant first.
          </div>
        )}
      </SelectContent>
    </Select>
  );
}

interface OrganizationSelectProps extends BaseProps {
  tenantId?: string;
}

export function OrganizationSelect({
  value,
  onChange,
  tenantId,
  id,
  placeholder = 'Select organization…',
}: OrganizationSelectProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['platform', 'organizations', 'select', tenantId ?? 'all'],
    queryFn: () => organizationsService.list({ tenantId, pageSize: 100 }),
    enabled: tenantId !== undefined ? Boolean(tenantId) : true,
  });
  const items = data?.items ?? [];

  return (
    <Select
      value={value}
      onValueChange={onChange}
      disabled={isLoading || (tenantId !== undefined && !tenantId)}
    >
      <SelectTrigger id={id}>
        <SelectValue
          placeholder={
            error
              ? 'Failed to load'
              : tenantId === ''
                ? 'Pick a tenant first'
                : placeholder
          }
        />
      </SelectTrigger>
      <SelectContent>
        {items.map((o) => (
          <SelectItem key={o.id} value={o.id}>
            <span className="font-mono text-xs text-muted-foreground">{o.code}</span>
            <span className="ml-2">{o.name}</span>
          </SelectItem>
        ))}
        {!isLoading && items.length === 0 && (
          <div className="px-2 py-3 text-center text-xs text-muted-foreground">
            No organizations.
          </div>
        )}
      </SelectContent>
    </Select>
  );
}

interface ApiClientSelectProps extends BaseProps {
  tenantId?: string;
}

export function ApiClientSelect({
  value,
  onChange,
  tenantId,
  id,
  placeholder = 'Select API client…',
}: ApiClientSelectProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['platform', 'api-clients', 'select', tenantId ?? 'all'],
    queryFn: () => apiClientsService.list({ tenantId, pageSize: 100 }),
  });
  const items = data?.items ?? [];

  return (
    <Select value={value} onValueChange={onChange} disabled={isLoading}>
      <SelectTrigger id={id}>
        <SelectValue placeholder={error ? 'Failed to load' : placeholder} />
      </SelectTrigger>
      <SelectContent>
        {items.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            {c.name}
            <span className="ml-2 text-xs text-muted-foreground">
              · {c.purpose.replace(/_/g, ' ')}
            </span>
          </SelectItem>
        ))}
        {!isLoading && items.length === 0 && (
          <div className="px-2 py-3 text-center text-xs text-muted-foreground">
            No API clients yet.
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
